import { mapLeaveRequestApiToInsight } from "../lib/leaveApprovalWorkflow";

export const LEAVE_DESK_PREFIX = "leave_req_";
export const LEAVE_ACTION_LABEL = "Leave";
export const LEAVE_CANCELLATION_ACTION_LABEL = "Leave cancellation";

export function isLeaveActionLabel(label) {
  const t = String(label || "").trim();
  return (
    t === LEAVE_ACTION_LABEL ||
    t === "Leave Request" ||
    t === LEAVE_CANCELLATION_ACTION_LABEL ||
    t === "Leave Cancellation"
  );
}

/** Console / desk title: never show "Leave Request". */
export function displayLeaveActionLabel(label) {
  const t = String(label || "").trim();
  if (t === "Leave Request" || t === LEAVE_ACTION_LABEL) return LEAVE_ACTION_LABEL;
  if (t === "Leave Cancellation") return LEAVE_CANCELLATION_ACTION_LABEL;
  return t;
}

/** Type=Leave includes Leave cancellation so ESS Approved is not emptied after cancel. */
export function matchesDeskSelectedType(actionLabel, selectedType) {
  const selected = String(selectedType || "").trim();
  const label = String(actionLabel || "").trim();
  if (!selected || selected === "All") return true;
  if (selected === LEAVE_ACTION_LABEL || selected === "Leave Request") {
    return isLeaveActionLabel(label);
  }
  return label === selected;
}

export const isLeaveDeskRequest = (request) =>
  request?.deskMode === "leave" ||
  (isLeaveActionLabel(request?.actionLabel) &&
    leaveRequestIdFromDeskId(request?.requestId) != null);

/** Resolve numeric leave_requests.id from desk requestId (number or leave_req_123). */
export function leaveRequestIdFromDeskId(requestId) {
  const raw = String(requestId ?? "").trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return Number(raw);
  if (raw.startsWith(LEAVE_DESK_PREFIX)) {
    const id = Number(raw.slice(LEAVE_DESK_PREFIX.length));
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  return null;
}

function linkedCancellationFromApi(row) {
  const src = row?.linkedCancellation;
  if (!src) return null;
  const mapped = mapLeaveRequestApiToInsight(src);
  if (!mapped) return null;
  const lastActed = (mapped.workflowTrail || []).find((s) => {
    const st = String(s?.status || "").toLowerCase();
    return st === "approved" || st === "rejected";
  });
  return {
    leaveRequestId: mapped.leaveRequestId,
    requestId: mapped.requestId,
    status: mapped.status,
    apiStatus: mapped.apiStatus,
    reason: mapped.formData?.reason || src.formData?.reason || "",
    notes: mapped.formData?.notes || src.formData?.notes || "",
    workflowTrail: mapped.workflowTrail || [],
    submittedAt: mapped.submittedAt,
    updatedAt: src.updatedAt,
    actorName: lastActed?.displayText || lastActed?.actor || null,
  };
}

export function mapLeaveRequestToDeskItem(row, { panelKey = "review" } = {}) {
  const mapped = mapLeaveRequestApiToInsight(row);
  if (!mapped) return null;

  const submittedAtMs = row.submittedAt
    ? new Date(row.submittedAt).getTime()
    : row.createdAt
      ? new Date(row.createdAt).getTime()
      : Date.now();

  const leaveId = mapped.leaveRequestId ?? mapped.requestId ?? row.id;
  return {
    ...mapped,
    action: displayLeaveActionLabel(mapped.action),
    actionLabel: displayLeaveActionLabel(mapped.actionLabel),
    requestId: `leave_req_${leaveRequestIdFromDeskId(mapped.requestId) || leaveId}`,
    deskMode: "leave",
    panelKey,
    userId: row.submittedByUserId ?? row.submittedBy?.id ?? null,
    userName: mapped.employeeName || "Employee",
    employeeCode: mapped.employeeCode || row.employeeCode || "",
    userEmail: "",
    submittedAt: submittedAtMs,
    updatedAt: row.updatedAt ? new Date(row.updatedAt).getTime() : submittedAtMs,
    canAct: row.canAct === true,
    leaveRequestId: leaveRequestIdFromDeskId(mapped.requestId) || leaveId,
    pendingWith: mapped.pendingWith || mapped.pendingAt || null,
    pendingAssigneeDisplay: mapped.pendingAssigneeDisplay || null,
    pendingLabel: mapped.pendingLabel || null,
    pendingRole: mapped.pendingRole || null,
    workflowTrail: mapped.workflowTrail || [],
    requestType: mapped.requestType || row.requestType || "leave",
    parentLeaveRequestId:
      mapped.parentLeaveRequestId || row.parentLeaveRequestId || null,
    cancelledAfterApproval: !!mapped.cancelledAfterApproval,
    openCancellationRequestId:
      mapped.openCancellationRequestId || row.openCancellationRequestId || null,
    canRequestCancellation:
      mapped.canRequestCancellation === true || row.canRequestCancellation === true,
    linkedCancellation: linkedCancellationFromApi(row),
  };
}

/**
 * Merge employee's own leave history (Request panel) with Approvals-desk leave
 * (pending + already acted by the signed-in approver).
 */
export function buildLeaveDeskItems({
  mine = [],
  pendingMyAction = [],
  approvalsDesk = [],
  employeeId = null,
} = {}) {
  const items = [];
  const ownIds = new Set();
  const approvalIds = new Set();

  for (const row of mine) {
    const item = mapLeaveRequestToDeskItem(row, { panelKey: "review" });
    if (!item) continue;
    ownIds.add(String(item.leaveRequestId));
    items.push(item);
  }

  const approvalSource =
    Array.isArray(approvalsDesk) && approvalsDesk.length
      ? approvalsDesk
      : pendingMyAction;

  for (const row of approvalSource) {
    const id = String(
      leaveRequestIdFromDeskId(row.id ?? row.requestId) ??
        row.id ??
        row.requestId ??
        ""
    );
    if (!id || ownIds.has(id) || approvalIds.has(id)) continue;
    const item = mapLeaveRequestToDeskItem(row, { panelKey: "approvals" });
    if (item) {
      approvalIds.add(String(item.leaveRequestId));
      items.push(item);
    }
  }

  const byId = new Map();
  for (const item of items) {
    const id = Number(item.leaveRequestId);
    if (Number.isFinite(id) && id > 0) byId.set(id, item);
  }
  for (const item of items) {
    if (String(item.requestType || "").toLowerCase() !== "cancellation") continue;
    const parent = byId.get(Number(item.parentLeaveRequestId));
    if (!parent) continue;
    const prev = parent.linkedCancellation;
    if (!prev || Number(item.leaveRequestId) > Number(prev.leaveRequestId)) {
      const lastActed = (item.workflowTrail || []).find((s) => {
        const st = String(s?.status || "").toLowerCase();
        return st === "approved" || st === "rejected";
      });
      parent.linkedCancellation = {
        leaveRequestId: item.leaveRequestId,
        requestId: item.requestId,
        status: item.status,
        apiStatus: item.apiStatus,
        reason: item.formData?.reason || "",
        notes: item.formData?.notes || "",
        workflowTrail: item.workflowTrail || [],
        submittedAt: item.submittedAt,
        updatedAt: item.updatedAt,
        actorName: lastActed?.displayText || lastActed?.actor || null,
      };
    }
  }

  // Keep the original Leave card. Hide a completed cancellation row only when
  // its parent is in this list (nested under that leave). Otherwise keep it so
  // Admin / RM Approvals still see cancellations they approved.
  return items.filter((item) => {
    if (String(item.requestType || "").toLowerCase() !== "cancellation") return true;
    if (item.status !== "Approved") return true;
    return !byId.has(Number(item.parentLeaveRequestId));
  });
}

export function leaveLinkedCancellationCaption(linked) {
  if (!linked) return "";
  const s = String(linked.status || "").toLowerCase();
  if (s === "approved") return "Leave cancellation approved";
  if (s === "rejected") return "Leave cancellation rejected";
  if (s === "pending") return "Leave cancellation pending";
  return "Leave cancellation";
}
